import React from "react";
import {
  MDBBadge,
  MDBIcon,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
} from "mdb-react-ui-kit";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import "./SharedExpenseSummary.css";

/**
 * SharedExpenseSummary
 *
 * Muestra el resumen de balances de una cuenta SHARED_EXPENSE:
 *   - Tabla: Usuario | % Cuenta | Balance
 *   - Sección "Ajuste de cuentas" con las transferencias mínimas necesarias.
 *
 * Si la cuenta no es SHARED_EXPENSE el componente no renderiza nada.
 *
 * Props:
 *   idCuenta   {number}    ID de la cuenta a resumir.
 *   onLoading  {Function}  Callback de carga del padre.
 */
export class SharedExpenseSummary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      participantes: [],
      deudas: [],
      loaded: false,
      applicable: false,
    };
  }

  componentDidMount() {
    if (this.props.idCuenta) {
      this.loadSummary();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.idCuenta !== this.props.idCuenta && this.props.idCuenta) {
      this.loadSummary();
    }
  }

  loadSummary() {
    this.props.onLoading(true);

    doFetch("CCON", `getSharedExpenseSummary?IDCuenta=${this.props.idCuenta}`, {
      _silent: true, // No mostrar toast 401 si no es SHARED_EXPENSE
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.STATE === "SUCCESS") {
          this.setState({
            participantes: data.DATA?.participantes ?? [],
            deudas: data.DATA?.deudas ?? [],
            loaded: true,
            applicable: true,
          });
        } else {
          // NOT_APPLICABLE o cualquier otro state → no mostrar
          this.setState({ loaded: true, applicable: false });
        }
      })
      .catch(() => {
        this.setState({ loaded: true, applicable: false });
      })
      .finally(() => {
        this.props.onLoading(false);
      });
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  }

  render() {
    const { loaded, applicable, participantes, deudas } = this.state;

    if (!loaded || !applicable) return null;

    return (
      <div className="shared-expense-summary">
        <div className="shared-summary-header">
          <MDBIcon fas icon="balance-scale" className="me-2" />
          <span>Balance de gastos compartidos</span>
        </div>

        {/* ── Tabla de balances ── */}
        <MDBTable striped hover responsive className="shared-summary-table">
          <MDBTableHead>
            <tr>
              <th>Usuario</th>
              <th className="text-center">% Participación</th>
              <th className="text-end">Balance</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {participantes.map((p, i) => (
              <tr key={i}>
                <td>{p.nombre}</td>
                <td className="text-center">
                  <MDBBadge color="secondary" pill light>
                    {Number(p.cuentaParticipacion).toFixed(0)} %
                  </MDBBadge>
                </td>
                <td className="text-end">
                  <MDBBadge
                    color={p.balance >= 0 ? "success" : "danger"}
                    pill
                  >
                    {this.formatCurrency(p.balance)}
                  </MDBBadge>
                </td>
              </tr>
            ))}
          </MDBTableBody>
        </MDBTable>

        {/* ── Ajuste de cuentas ── */}
        {deudas.length > 0 && (
          <div className="shared-debts">
            <div className="shared-debts-title">
              <MDBIcon fas icon="arrows-alt-h" className="me-2" />
              Ajuste de cuentas
            </div>
            <ul className="shared-debts-list">
              {deudas.map((d, i) => (
                <li key={i}>
                  <span className="debt-from">{d.de}</span>
                  <MDBIcon fas icon="long-arrow-alt-right" className="mx-2 text-muted" />
                  <span className="debt-to">{d.a}</span>
                  <MDBBadge color="warning" pill light className="ms-2">
                    {this.formatCurrency(d.cantidad)}
                  </MDBBadge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {deudas.length === 0 && (
          <p className="text-muted text-center small mt-2">
            <MDBIcon fas icon="check-circle" className="text-success me-1" />
            Sin deudas pendientes.
          </p>
        )}
      </div>
    );
  }
}
